'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { ReadOnlyListing } from 'hotdesk_typescript';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import {
    ComparePick,
    exitCompareMode,
    removePick,
    setCompareType,
    startCompareMode,
    togglePick
} from '@/hotdesk-ui/store/slices/compare-selection.slice';
import {
    CompareAddSource,
    CompareEntryPoint,
    CompareExitMethod,
    CompareRemoveSource,
    CompareType
} from '@/hotdesk-ui/enum/compare';
import { COMPARE_MIN_ITEMS } from '@/hotdesk-ui/lib/compare/constants';
import { buildCompareHref } from '@/hotdesk-ui/lib/compare/url-state';
import { useCompareConfig } from './useCompareConfig';
import { useCompareTracking } from './useCompareTracking';

/** What a card needs to show and handle compare mode. */
export interface CompareCardState {
    picked: boolean;
    /** The limit is reached and this card is not picked: dimmed, cannot be picked. */
    disabled: boolean;
    onToggle: () => void;
}

/**
 * An Explore card is backed by a listing. In workspace mode the card stands
 * for its workspace, so the pick is the workspace.
 */
export const toComparePick = (
    item: ReadOnlyListing,
    type: CompareType
): ComparePick | null => {
    if (type === CompareType.WORKSPACE) {
        const id = item.workspace?.id;
        return id ? { id, name: item.workspace?.title || item.title || '' } : null;
    }
    return item.id
        ? { id: item.id, name: item.title || item.workspace?.title || '' }
        : null;
};

/**
 * Explore compare mode (PRD § Compare mode on the Explore page): the toggle,
 * card picking and the floating bar all read from here.
 */
export function useExploreCompare(compareType: CompareType, view: string) {
    const dispatch = useAppDispatch();
    const router = useRouter();
    const { enabled, maxItems, entryPoints } = useCompareConfig();
    const track = useCompareTracking(compareType);
    const { active, picks } = useAppSelector(
        (state: RootState) => state.compareSelection
    );

    // The results switched between workspaces and listings: a comparison
    // never mixes the two, so earlier picks are dropped.
    useEffect(() => {
        if (active) dispatch(setCompareType(compareType));
    }, [active, compareType, dispatch]);

    // Leaving the Explore page ends compare mode.
    useEffect(() => () => {
        dispatch(exitCompareMode());
    }, [dispatch]);

    const atLimit = picks.length >= maxItems;
    const canCompare = picks.length >= COMPARE_MIN_ITEMS;

    const start = useCallback(() => {
        dispatch(startCompareMode(compareType));
        track('compare_mode_started', 0, { view });
    }, [dispatch, compareType, track, view]);

    const exit = useCallback(
        (method: CompareExitMethod) => {
            track('compare_mode_exited', picks.length, { method });
            dispatch(exitCompareMode());
        },
        [dispatch, track, picks.length]
    );

    const remove = useCallback(
        (id: number, source: CompareRemoveSource) => {
            dispatch(removePick(id));
            track('compare_item_removed', picks.length - 1, {
                source,
                item_id: id
            });
        },
        [dispatch, track, picks.length]
    );

    const toggleItem = useCallback(
        (item: ReadOnlyListing) => {
            const pick = toComparePick(item, compareType);
            if (!pick) return;
            if (picks.some((p) => p.id === pick.id)) {
                remove(pick.id, CompareRemoveSource.EXPLORE_CARD);
                return;
            }
            if (atLimit) return;
            dispatch(togglePick({ pick, maxItems }));
            const count = picks.length + 1;
            track('compare_item_added', count, {
                source: CompareAddSource.EXPLORE,
                item_id: pick.id
            });
            if (count === maxItems) {
                track('compare_limit_reached', count, {
                    source: CompareAddSource.EXPLORE
                });
            }
        },
        [compareType, picks, atLimit, maxItems, dispatch, track, remove]
    );

    const cardState = useCallback(
        (item: ReadOnlyListing): CompareCardState | undefined => {
            if (!active) return undefined;
            const pick = toComparePick(item, compareType);
            const picked = !!pick && picks.some((p) => p.id === pick.id);
            return {
                picked,
                disabled: !picked && atLimit,
                onToggle: () => toggleItem(item)
            };
        },
        [active, compareType, picks, atLimit, toggleItem]
    );

    const openComparison = useCallback(() => {
        if (!canCompare) return;
        // Columns follow pick order: the first one picked is "compare against".
        // `back` keeps this Explore search (location, filters, view), so
        // "Back to Explore" lands on the same results.
        router.push(
            buildCompareHref(compareType, {
                items: picks.map((p) => p.id),
                from: CompareEntryPoint.EXPLORE,
                back: `${window.location.pathname}${window.location.search}`
            })
        );
    }, [canCompare, router, compareType, picks]);

    return useMemo(
        () => ({
            /** Remote config can switch the Explore entry point off on its own. */
            enabledHere: enabled && entryPoints.includes(CompareEntryPoint.EXPLORE),
            /** What this comparison holds — workspaces or listings, never both. */
            type: compareType,
            active,
            picks,
            maxItems,
            atLimit,
            canCompare,
            start,
            exit,
            remove,
            cardState,
            openComparison
        }),
        [enabled, entryPoints, compareType, active, picks, maxItems, atLimit, canCompare, start, exit, remove, cardState, openComparison]
    );
}

export type ExploreCompare = ReturnType<typeof useExploreCompare>;
